/**
 * Shared Search-page banner metrics and background treatment.
 * Light mode keeps the solid wash fill. Dark mode uses today's muted
 * charcoal banner gradient from palette.banner, not secondary.main.
 */

import {
  DARK_PAGE_BACKGROUND,
  getDarkModeBannerGradient,
  getDarkModeBannerGradientExtended,
  getDarkModeBannerGradientSoft,
  getDarkModeFilterBandGradient,
  getLightModeBannerGradient,
  getLightModeBannerGradientExtended,
  getLightModeBannerGradientSoft,
  getLightModeFilterBandGradient,
} from "../../../theme/colorTokens";

export function getSubHeaderBannerMetrics(isMobile) {
  return {
    minHeight: isMobile ? 52 : 94,
    padding: isMobile ? "10px 16px 10px 16px" : "19px 120px 19px 48px",
    titleFontSize: isMobile ? 16 : 24,
    titleLineHeight: isMobile ? "22px" : "30px",
    titleFontWeight: isMobile ? 700 : 800,
  };
}

export function getSubHeaderBannerBackground(theme) {
  if (theme.palette.mode === "dark") {
    if (theme.palette.banner?.gradient) {
      return theme.palette.banner.gradient;
    }
    return getDarkModeBannerGradient(theme.palette.background?.default);
  }

  if (theme.palette.secondary?.main) {
    return theme.palette.secondary.main;
  }

  if (theme.palette.banner?.gradient) {
    return theme.palette.banner.gradient;
  }

  return getLightModeBannerGradient();
}

/**
 * Filter toolbar band: light wash fading into the page, or dark ease from
 * the banner's lower edge into the page canvas.
 */
export function getFilterBandBackground(theme) {
  if (theme.palette.banner?.filterBand) {
    return theme.palette.banner.filterBand;
  }

  const pageBg =
    theme.palette.background?.default ??
    (theme.palette.mode === "dark" ? DARK_PAGE_BACKGROUND : "#ffffff");

  if (theme.palette.mode === "dark") {
    return getDarkModeFilterBandGradient(pageBg);
  }

  if (theme.palette.secondary?.main) {
    return `linear-gradient(180deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.main} 60%, ${pageBg} 100%)`;
  }

  return getLightModeFilterBandGradient(pageBg);
}

export function getBannerGradientSoft(theme) {
  if (theme.palette.banner?.gradientSoft) {
    return theme.palette.banner.gradientSoft;
  }

  if (theme.palette.mode === "dark") {
    return getDarkModeBannerGradientSoft();
  }

  if (theme.palette.secondary?.main) {
    return `linear-gradient(180deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.main} 55%, transparent 100%)`;
  }

  return getLightModeBannerGradientSoft();
}

export function getBannerGradientExtended(theme) {
  if (theme.palette.banner?.gradientExtended) {
    return theme.palette.banner.gradientExtended;
  }

  const pageBg =
    theme.palette.background?.default ??
    (theme.palette.mode === "dark" ? DARK_PAGE_BACKGROUND : "#ffffff");

  if (theme.palette.mode === "dark") {
    return getDarkModeBannerGradientExtended(pageBg);
  }

  if (theme.palette.secondary?.main) {
    return `linear-gradient(180deg, ${theme.palette.secondary.main} 0px, ${theme.palette.secondary.main} 120px, ${pageBg} 220px, ${pageBg} 100%)`;
  }

  return getLightModeBannerGradientExtended(pageBg);
}

export function getBannerFlatBackground(theme) {
  if (theme.palette.banner?.flat) {
    return theme.palette.banner.flat;
  }

  if (theme.palette.mode === "dark") {
    return getDarkModeBannerGradient(theme.palette.background?.default);
  }

  if (theme.palette.secondary?.main) {
    return theme.palette.secondary.main;
  }

  return getLightModeBannerGradient();
}
